import { atom } from 'recoil';
import { CouchVariant } from '~/domain/couchtop/couchLibrary';
import { DicomColor } from '~/domain/dicom/dicomColor';

/**
 * A couch-top placement: the continuous voxel Y, in the image's own grid,
 * at which the library cross-section's y = 0 edge sits. Kept in voxel
 * space rather than world mm because which patient axis carries the depth
 * follows the series' direction cosines — see the frame note in
 * `~/domain/couchtop/geometry`.
 */
export interface CouchTopRow {
    readonly voxelY: number;
}

/**
 * THE store for couch-top placements, keyed by the SeriesInstanceUID the
 * placement was made AGAINST. A placement is only meaningful against its
 * own volume — applied to another series it would put the couch at a
 * plausible but wrong depth — so the lookup key IS the isolation: a
 * reader asks for its own series and can never be handed another's.
 *
 * Session state only, and SHORTER-lived than the session: never written
 * to DICOM, never tracked (it lives outside the domain entirely, so it
 * cannot reach the change tracker), discarded when the couch panel closes
 * (see `useCouchTopRowReset`, wired in `CouchOperationContent`) so an
 * abandoned placement cannot be applied later or carried onto a different
 * structure set, and reset as a whole by `useUnloadPatient` so it cannot
 * outlive its patient. Read and written ONLY through
 * `~/hooks/useCouchTopRow`.
 */
export const couchTopRowsState = atom<ReadonlyMap<string, CouchTopRow>>({
    key: 'couchTopRowsState',
    default: new Map(),
});

/**
 * What the couch panel currently has selected, for the viewport's
 * uncommitted curve preview (`CouchTopLine`) to draw — nothing is written
 * from this; the preview reads it purely to know what to ghost. `null`
 * until the panel's Preview button is pressed — the ghost is explicit,
 * like the body-contour panel's — and again when no row exists, when the
 * variant/model selection changes, or when the panel is closed. Kept
 * separate from `couchTopRowsState`: the row is clinical placement data
 * the whole app can read, this is transient panel UI state that only the
 * preview needs.
 *
 * Carries ONLY what the ghost consumes — the variant's geometry and one
 * colour per component (`structureColors`, aligned 1:1 by index with
 * `variant.components`). Deliberately NOT the panel's editable names:
 * the ghost never draws them, and a name here would be dead state a
 * future reader could mistake for a validated value.
 */
export interface CouchModelPreviewSelection {
    readonly variant: CouchVariant;
    readonly structureColors: readonly DicomColor[];
}

export const couchModelPreviewSelectionState =
    atom<CouchModelPreviewSelection | null>({
        key: 'couchModelPreviewSelectionState',
        default: null,
    });
